import React from 'react';
import { Box } from '@mui/material';

// Shared Components
import SolutionHero from '../components/SolutionHero';
import ServicesGrid from '../components/ServicesGrid';
import ProcessTimeline from '../components/ProcessTimeline';
import BenefitsSection from '../components/BenefitsSection';
import FAQAccordion from '../components/FAQAccordion';
import SolutionCTA from '../components/SolutionCTA';

// Icons
import InsightsIcon from '@mui/icons-material/Insights';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';

const StrategicConsulting = () => {
    // Page Content
    const services = [
        {
            icon: <InsightsIcon sx={{ fontSize: 28 }} />,
            title: "AI Readiness Assessment",
            description: "Evaluate your data maturity, infrastructure, and team capabilities to identify the highest-impact AI opportunities for your business."
        },
        {
            icon: <PsychologyIcon sx={{ fontSize: 28 }} />,
            title: "Use Case Prioritization",
            description: "Map feasibility against business impact to identify quick wins and long-term strategic AI investments."
        },
        {
            icon: <AccountTreeIcon sx={{ fontSize: 28 }} />,
            title: "GenAI Strategy & Roadmaps",
            description: "Build a comprehensive roadmap for LLM adoption, RAG architectures, and generative AI integration across your enterprise."
        },
        {
            icon: <IntegrationInstructionsIcon sx={{ fontSize: 28 }} />,
            title: "ML/LLM Architecture Design",
            description: "Design scalable, production-ready architectures for machine learning and large language model deployments."
        },
        {
            icon: <SpeedIcon sx={{ fontSize: 28 }} />,
            title: "Vendor & Tool Evaluation",
            description: "Navigate the AI landscape with expert guidance on platforms, models, and tooling that fit your needs and budget."
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 28 }} />,
            title: "AI Governance Frameworks",
            description: "Establish policies, compliance measures, and ethical guidelines for responsible AI deployment."
        }
    ];

    const processSteps = [
        {
            title: "Discovery",
            description: "Deep dive into your business goals, current capabilities, data landscape, and existing technology stack."
        },
        {
            title: "Strategy",
            description: "Develop prioritized use cases, build business cases, and align stakeholders on the AI vision."
        },
        {
            title: "Architecture",
            description: "Design technical architecture, data pipelines, and integration patterns for production-ready AI."
        },
        {
            title: "Roadmap Delivery",
            description: "Deliver a detailed implementation plan with timelines, budgets, and success metrics."
        }
    ];

    const benefits = [
        {
            title: "Production-First Mindset",
            description: "We focus on the 'Ships' part of 'Build AI That Actually Ships.' Every recommendation is designed for real-world deployment."
        },
        {
            title: "Speed-to-Value",
            description: "Our modular framework delivers actionable roadmaps in 2-4 weeks, not months. Start seeing ROI faster."
        },
        {
            title: "Cloud Agnostic",
            description: "We work across AWS, GCP, Azure, and hybrid environments. No vendor lock-in, maximum flexibility."
        },
        {
            title: "Security & Compliance First",
            description: "SOC2-ready architectures, data privacy protocols, and enterprise-grade security built into every design."
        }
    ];

    const faqs = [
        {
            question: "How long does a typical AI strategy engagement take?",
            answer: "Most strategy engagements run 2-4 weeks for a complete roadmap. This includes discovery sessions, stakeholder interviews, use case workshops, and deliverable reviews. Complex enterprise engagements may extend to 6-8 weeks."
        },
        {
            question: "Do you work with existing technology stacks?",
            answer: "Absolutely. We're cloud-agnostic and technology-neutral. Whether you're on AWS, GCP, Azure, or a hybrid setup, we design solutions that integrate with your current infrastructure and tech investments."
        },
        {
            question: "What industries do you specialize in?",
            answer: "We have deep experience in Financial Services, Healthcare, Retail/E-commerce, Manufacturing, and Logistics. However, our frameworks are adaptable to any industry facing AI transformation."
        },
        {
            question: "How do you ensure data privacy and security?",
            answer: "We design with security-first principles: on-premise options for sensitive data, PII anonymization, role-based access controls, and compliance with HIPAA, GDPR, and SOC2 requirements."
        },
        {
            question: "What deliverables can we expect?",
            answer: "Typical deliverables include: AI Readiness Assessment Report, Prioritized Use Case Matrix, Technical Architecture Diagrams, Implementation Roadmap, and Budget/Resource Estimates."
        }
    ];

    const ctaStats = [
        { value: "50+", label: "AI Projects Delivered" },
        { value: "2-4", label: "Weeks to Roadmap" },
        { value: "10x", label: "Average ROI" },
        { value: "100%", label: "Production Success Rate" }
    ];

    return (
        <Box sx={{ bgcolor: '#030014', minHeight: '100vh' }}>
            {/* Hero Section */}
            <SolutionHero
                headline="Turn AI Ambition Into"
                highlightedText="Production Reality"
                subheadline="From roadmap to ROI in weeks—we assess, strategize, and architect AI solutions that actually ship. No PowerPoint promises, just production-grade strategies."
                primaryCTA="Book a Strategy Session"
                primaryCTALink="/contact-us"
                secondaryCTA="View Our Process"
                secondaryCTALink="#process"
            />

            {/* Services Grid */}
            <ServicesGrid
                title="Strategic AI Services"
                subtitle="What We Offer"
                services={services}
            />

            {/* Process Timeline */}
            <ProcessTimeline
                title="Our Proven Methodology"
                subtitle="How We Work"
                steps={processSteps}
            />

            {/* Benefits Section */}
            <BenefitsSection
                title="The Beanbag AI Advantage"
                subtitle="Why Choose Us"
                benefits={benefits}
            />

            {/* FAQ Section */}
            <FAQAccordion
                title="Frequently Asked Questions"
                subtitle="Got Questions?"
                faqs={faqs}
            />

            {/* CTA Section */}
            <SolutionCTA
                headline="Ready to Build AI That Actually Works?"
                subheadline="Stop experimenting and start shipping. Let's build your intelligent enterprise together."
                primaryCTA="Start Your Journey"
                primaryCTALink="/contact-us"
                secondaryCTA="View Case Studies"
                secondaryCTALink="/case-studies"
                stats={ctaStats}
            />
        </Box>
    );
};

export default StrategicConsulting;
